<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import PhoneSettingsAPI from 'dashboard/api/phoneSettings';

// Polled rather than pushed: the bridge has no socket into Chatwoot, and a
// ringing call only lasts seconds, so a short interval is the honest trade.
const POLL_MS = 3000;

const accountId = useMapGetter('getCurrentAccountId');
const waiting = ref([]);
const dismissed = ref(new Set());
let timer = null;

const call = computed(
  () => waiting.value.find(c => !dismissed.value.has(c.call_control_id)) || null
);

const isConsult = computed(() => Boolean(call.value?.is_consult));
const consultFrom = computed(() => call.value?.consult_from || '');

const callerNumber = computed(() => {
  const from = call.value?.from || '';
  // Show it the way a person would read it back.
  const digits = from.replace(/[^\d]/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  return from || 'Unknown number';
});

const waitedFor = computed(() => {
  const seconds = call.value?.elapsed_seconds || 0;
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
});

const poll = async () => {
  // The account id arrives with the current user, a moment after mount.
  // Polling before then builds /accounts/null/... and 404s on every load.
  if (!accountId.value) return;
  try {
    const { data } = await PhoneSettingsAPI.ringing(accountId.value);
    // A colleague ringing you for a warm transfer is an incoming call
    // too, and the more urgent one - show those first.
    waiting.value = [...(data.incoming || []), ...(data.waiting || [])];
    // Forget dismissals for calls that have since ended, so the set cannot
    // grow without bound across a long shift.
    const live = new Set(waiting.value.map(c => c.call_control_id));
    dismissed.value = new Set([...dismissed.value].filter(id => live.has(id)));
  } catch (error) {
    waiting.value = [];
  }
};

const dismiss = () => {
  if (call.value) dismissed.value = new Set([...dismissed.value, call.value.call_control_id]);
};

// Answering happens in the phone panel, which owns the WebRTC leg. Opening it
// is the honest handoff rather than duplicating the answer logic here.
const openPhone = () => {
  // 2026-10-27: answer in one click - the dock forwards the call id to the
  // panel, which takes the call (phone-panel:answer). The dock still opens so
  // the agent sees the live call controls.
  const ccid = call.value && call.value.call_control_id;
  window.dispatchEvent(new CustomEvent('phone-dock:open'));
  if (ccid) window.dispatchEvent(new CustomEvent('phone-dock:answer', { detail: { ccid } }));
  dismiss();
};

onMounted(() => {
  poll();
  timer = setInterval(poll, POLL_MS);
});
onBeforeUnmount(() => clearInterval(timer));
</script>

<template>
  <Transition name="ring">
    <div v-if="call" class="ring-toast">
      <div class="ring-pulse" />
      <div class="ring-body">
        <div class="ring-label">
          {{
            isConsult
              ? $t('PHONE_SETTINGS.INCOMING.CONSULT_TITLE')
              : $t('PHONE_SETTINGS.INCOMING.TITLE')
          }}
        </div>
        <div class="ring-number">{{ callerNumber }}</div>
        <div class="ring-meta">
          {{
            isConsult
              ? $t('PHONE_SETTINGS.INCOMING.CONSULT_FROM', { agent: consultFrom })
              : $t('PHONE_SETTINGS.INCOMING.WAITING', { time: waitedFor })
          }}
        </div>
      </div>
      <div class="ring-actions">
        <button type="button" class="ring-answer" @click="openPhone">
          {{ $t('PHONE_SETTINGS.INCOMING.ANSWER') }}
        </button>
        <button type="button" class="ring-dismiss" @click="dismiss">
          {{ $t('PHONE_SETTINGS.INCOMING.DISMISS') }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.ring-toast {
  position: fixed;
  right: 16px;
  bottom: 80px;
  z-index: 9995;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 340px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #0f7a3d;
  color: #fff;
  box-shadow: 0 10px 34px rgba(0, 0, 0, 0.3);
}

/* A quiet pulse reads as "ringing" without an animation that fights for
   attention every three seconds. */
.ring-pulse {
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #fff;
  animation: ring-pulse 1.4s ease-in-out infinite;
}
@keyframes ring-pulse {
  0%, 100% { opacity: 0.35; transform: scale(0.85); }
  50% { opacity: 1; transform: scale(1); }
}

.ring-body { flex: 1; min-width: 0; }
.ring-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.85;
}
.ring-number {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ring-meta { font-size: 11px; opacity: 0.8; }

.ring-actions { display: flex; flex-direction: column; gap: 6px; }
.ring-answer,
.ring-dismiss {
  border: 0;
  border-radius: 6px;
  padding: 6px 12px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.ring-answer { background: #fff; color: #0f7a3d; }
.ring-answer:hover { filter: brightness(0.95); }
.ring-dismiss {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-weight: 500;
}
.ring-dismiss:hover { background: rgba(255, 255, 255, 0.26); }

.ring-enter-active,
.ring-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.ring-enter-from,
.ring-leave-to { opacity: 0; transform: translateY(8px); }

@media (prefers-reduced-motion: reduce) {
  .ring-pulse { animation: none; opacity: 1; }
  .ring-enter-active, .ring-leave-active { transition: none; }
}
</style>
